import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { ReplaySubject, of } from "rxjs";
import { catchError, map, take } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";

import { AUTH_SERVICE_URL } from "environments/config";
import { Utils } from "app/models/general/utils";
import { ResponseModel } from "app/models/objects/general/response.model";
import { CustomerUserModel } from "app/models/objects/user/user.model";

@Injectable()
export class AuthService {
  me = new ReplaySubject<CustomerUserModel | null>(1);
  meObj: CustomerUserModel | null = null;

  constructor(private http: HttpClient, private translate: TranslateService) {}

  validate() {
    if (!Utils.getToken()) {
      this.me.next(null);
      this.meObj = null;
      return of(false);
    }

    return this.http.get<CustomerUserModel>(`${AUTH_SERVICE_URL}/me`).pipe(
      take(1),
      map((user) => {
        this.me.next(user);
        this.meObj = user;
        return true;
      }),
      catchError(() => {
        this.me.next(null);
        this.meObj = null;
        Utils.removeToken();
        return of(false);
      })
    );
  }

  login(identifier: string, password: string) {
    return this.http.post<any>(`${AUTH_SERVICE_URL}/login`, {
      identifier,
      password
    }).pipe(
      take(1),
      map((response) => {
        if (response.token) {
          Utils.setToken(response.token);
          return ResponseModel.create(true, "ok");
        }
        if (response.result) {
          return ResponseModel.create(false, response.result);
        }
        return ResponseModel.create(false, "login_failed");
      }),
      catchError((error) => of(ResponseModel.create(false, error?.error?.result || error?.error?.error || "login_failed")))
    );
  }

  logout() {
    Utils.removeToken();
    this.me.next(null);
    this.meObj = null;
    return of(true);
  }

  saveLanguage(lang: string) {
    Utils.setLanguage(lang);
    this.translate.use(lang);
    return this.http.post(`${AUTH_SERVICE_URL}/me/language?lang=${lang}`, {}).pipe(
      take(1),
      map((response) => {
        if (this.meObj) {
          this.meObj = {
            ...this.meObj,
            language: lang
          };
          this.me.next(this.meObj);
        }
        return response;
      })
    );
  }

  changePassword(current: string, password: string, invalidateAll = false) {
    return this.http.put(`${AUTH_SERVICE_URL}/password`, {
      current,
      password,
      invalidate_all: invalidateAll
    }).pipe(take(1));
  }

  requestPasswordReset(identifier: string) {
    return this.http.post(`${AUTH_SERVICE_URL}/forgot`, {
      identifier
    }).pipe(take(1));
  }

  finishPasswordReset(identifier: string, code: string, password: string) {
    return this.http.post<any>(`${AUTH_SERVICE_URL}/recovery`, {
      identifier,
      code,
      password
    }).pipe(
      take(1),
      map((response) => {
        if (response?.token) {
          Utils.setToken(response.token);
          return true;
        }
        return false;
      })
    );
  }

  finishRecoveryByLink(identifier: string, password: string) {
    return this.http.post<any>(`${AUTH_SERVICE_URL}/recovery`, {
      identifier,
      password
    }).pipe(
      take(1),
      map((response) => {
        if (response?.token) {
          Utils.setToken(response.token);
          return true;
        }
        return false;
      })
    );
  }

  recoverAccount(payload: { pid: string; contact: string; phone?: string; email?: string; message?: string }) {
    return this.http.post(`${AUTH_SERVICE_URL}/account_recovery`, payload).pipe(take(1));
  }

  requestPhoneVerification() {
    return this.http.post(`${AUTH_SERVICE_URL}/me/phone_verification/request`, {}).pipe(take(1));
  }

  requestEmailChange(email: string) {
    return this.http.post(`${AUTH_SERVICE_URL}/me/email/change/request`, { email }).pipe(take(1));
  }

  confirmEmailChange(email: string, code: string) {
    return this.http.post<CustomerUserModel>(`${AUTH_SERVICE_URL}/me/email/change/confirm`, { email, code }).pipe(
      take(1),
      map((user) => {
        this.meObj = user;
        this.me.next(user);
        return user;
      })
    );
  }
}
